<template>
    <article>
        <h2>TL;DR</h2>

        <p>
            We introduced Continuous Profiling capabilities in Coroot with version 0.14 (Feb 2023).
            Initially, this feature relied on Pyroscope, an open-source continuous profiling platform.
            Later on, Pyroscope was acquired by Grafana Labs, resulting in a license change from Apache 2.0 to AGPL-3.0.
            Consequently, the team began integrating Pyroscope into Grafana's ecosystem, resulting in collateral storage and API changes.
        </p>

        <p>
            Upon discovering bugs in Pyroscope reported by our users, our initial plan was to contribute fixes to the Pyroscope codebase.
            However, realizing that even integrating the latest version of Pyroscope with Coroot would require significant effort,
            we opted to implement our own storage for profiling data based on ClickHouse.
            Since ClickHouse manages all tasks related to storing and retrieving data, our main tasks were implementing
            the right schema and querying logic. This approach has resulted in a much simpler solution, both in terms of support and maintenance.
        </p>

        <h2>A brief intro into Continuous Profiling</h2>

        <p>
            Continuous Profiling is a technique that involves recording the state of an application over time and using
            this data to answer questions like:
        </p>

        <ul>
            <li>Which functions are consuming the majority of CPU time?</li>
            <li>What piece of code is responsible for a spike in CPU consumption?</li>
            <li>Which part of the code is currently consuming the most memory?</li>
            <li>What functions are allocating significant memory, leading to Garbage Collection (GC) pressure?</li>
        </ul>

        <p>
            Typically, profiling data is visualized through FlameGraphs.
            These graphs displays the code hierarchy organized by resource consumption (CPU, Memory).
            Each frame on the FlameGraph represents the amount of resources consumed by a specific function.
            A wider frame signifies greater resource consumption by that function, with frames underneath indicating nested function calls.
        </p>

        <div class="pa-5 my-6">
            <v-img eager class="ma-auto" src="/static/img/blog/profiles-in-clickhouse/flamegraph.png" />
        </div>

        <h2>Profiling data model</h2>

        <p>
            Roughly speaking, a profile is a set of measurements, each represented by a value and linked to a corresponding call stack.
            For example, we observe two call stacks that share two common parts.
        </p>

        <v-simple-table dense class="mb-4">
            <thead>
                <tr>
                    <td>Value</td>
                    <td>Call stack</td>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>5 seconds</td>
                    <td>main&rarr;handleRequest&rarr;serializeResponse&rarr;json.Marshall</td>
                </tr>
                <tr>
                    <td>10 seconds</td>
                    <td>main&rarr;handleRequest&rarr;queryDatabase</td>
                </tr>
            </tbody>
        </v-simple-table>

        <p>
            The FlameGraph for this profile would look as follows:
        </p>

        <v-img eager contain class="ma-5" src="/static/img/blog/profiles-in-clickhouse/flamegraph_example.png" />

        <p>
            The <var>main</var> and <var>handleRequest</var> functions consumed a total of 15 seconds (5 + 10).
            This consumption was then distributed between <var>queryDatabase</var> and <var>serializeResponse</var>.
            Examining this FlameGraph makes it clear that <var>queryDatabase</var> consumes twice as much as <var>serializeResponse</var>.
            Pretty straightforward, isn't it?
        </p>

        <p>
            Now, let's consider profiling our app once a minute. Each measurement should be annotated with the relevant timestamp.
        </p>

        <v-simple-table dense class="mb-4">
            <thead>
            <tr>
                <td>Time</td>
                <td>Value</td>
                <td>Call stack</td>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td>60</td>
                <td>5 seconds</td>
                <td>main&rarr;handleRequest&rarr;serializeResponse&rarr;json.Marshall</td>
            </tr>
            <tr>
                <td>60</td>
                <td>10 seconds</td>
                <td>main&rarr;handleRequest&rarr;queryDatabase</td>
            </tr>
            <tr>
                <td>120</td>
                <td>3 seconds</td>
                <td>main&rarr;handleRequest&rarr;serializeResponse&rarr;json.Marshall</td>
            </tr>
            <tr>
                <td>120</td>
                <td>7 seconds</td>
                <td>main&rarr;handleRequest&rarr;queryDatabase</td>
            </tr>
            </tbody>
        </v-simple-table>

        <p>
            To render the aggregated FlameGraph, we need to sum the values for the same corresponding call stacks.
        </p>

        <v-img eager contain class="ma-5" src="/static/img/blog/profiles-in-clickhouse/flamegraph_example2.png" />

        <p>
            If we are profiling multiple applications in our cluster, we need a way to distinguish their profiles.
            Therefore, let's add labels for each measurement:
        </p>

        <v-simple-table dense class="mb-4">
            <thead>
            <tr>
                <td>Time</td>
                <td>Labels</td>
                <td>Value</td>
                <td>Call stack</td>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td>60</td>
                <td>{ns: default, pod: catalog-xxx-111}</td>
                <td>5 seconds</td>
                <td>main&rarr;handleRequest&rarr;serializeResponse&rarr;json.Marshall</td>
            </tr>
            <tr>
                <td>60</td>
                <td>{ns: default, pod: catalog-xxx-111}</td>
                <td>10 seconds</td>
                <td>main&rarr;handleRequest&rarr;queryDatabase</td>
            </tr>
            <tr>
                <td>60</td>
                <td>{ns: default, pod: catalog-xxx-2222}</td>
                <td>5 seconds</td>
                <td>main&rarr;handleRequest&rarr;serializeResponse&rarr;json.Marshall</td>
            </tr>
            <tr>
                <td>60</td>
                <td>{ns: default, pod: catalog-xxx-2222}</td>
                <td>10 seconds</td>
                <td>main&rarr;handleRequest&rarr;queryDatabase</td>
            </tr>
            <tr>
                <td>120</td>
                <td>{ns: default, pod: catalog-xxx-1111}</td>
                <td>3 seconds</td>
                <td>main&rarr;handleRequest&rarr;serializeResponse&rarr;json.Marshall</td>
            </tr>
            <tr>
                <td>120</td>
                <td>{ns: default, pod: catalog-xxx-1111}</td>
                <td>7 seconds</td>
                <td>main&rarr;handleRequest&rarr;queryDatabase</td>
            </tr>
            <tr>
                <td>120</td>
                <td>{ns: default, pod: catalog-xxx-2222}</td>
                <td>3 seconds</td>
                <td>main&rarr;handleRequest&rarr;serializeResponse&rarr;json.Marshall</td>
            </tr>
            <tr>
                <td>120</td>
                <td>{ns: default, pod: catalog-xxx-2222}</td>
                <td>7 seconds</td>
                <td>main&rarr;handleRequest&rarr;queryDatabase</td>
            </tr>
            </tbody>
        </v-simple-table>

        <p>
            Implementing this schema directly in ClickHouse doesn't seem efficient due to the repetition of potentially long call stacks.
            Our solution involves storing stacks in a separate table, and values reference them using short hashes.
        </p>

        <p>
            This approach significantly reduces the amount of data read from disk during queries,
            as each unique call stack is read only once after aggregating values.
            As a result, ClickHouse has to decompress less data, leading to lower CPU consumption and improved query latency.
        </p>

        <p>
            The <var>profiling_samples</var> table stores values along with their corresponding stack hashes (8 bytes per hash):
        </p>

        <pre v-hl.sql>
CREATE TABLE profiling_samples
(
    `ServiceName` LowCardinality(String) CODEC(ZSTD(1)),
    `Type` LowCardinality(String) CODEC(ZSTD(1)),
    `Start` DateTime64(9) CODEC(Delta(8), ZSTD(1)),
    `End` DateTime64(9) CODEC(Delta(8), ZSTD(1)),
    `Labels` Map(LowCardinality(String), String) CODEC(ZSTD(1)),
    `StackHash` UInt64 CODEC(ZSTD(1)),
    `Value` Int64 CODEC(ZSTD(1))
)
ENGINE = MergeTree
PARTITION BY toDate(Start)
ORDER BY (ServiceName, Type, toUnixTimestamp(Start), toUnixTimestamp(End))
TTL toDateTime(Start) + toIntervalDay(7)
SETTINGS index_granularity = 8192
        </pre>

        <p>
            Call stacks are stored in the <var>profiling_stacks</var> table.
            Since there's no need to store identical stacks multiple times, we can leverage the <var>ReplacingMergeTree</var> engine,
            which automatically eliminates repetitions from the table.
        </p>

        <pre v-hl.sql>
CREATE TABLE profiling_stacks
(
    `ServiceName` LowCardinality(String) CODEC(ZSTD(1)),
    `Hash` UInt64 CODEC(ZSTD(1)),
    `LastSeen` DateTime64(9) CODEC(Delta(8), ZSTD(1)),
    `Stack` Array(String) CODEC(ZSTD(1))
)
ENGINE = ReplacingMergeTree
PARTITION BY toDate(LastSeen)
PRIMARY KEY (ServiceName, Hash)
ORDER BY (ServiceName, Hash)
TTL toDateTime(LastSeen) + toIntervalDay(7)
SETTINGS index_granularity = 8192
        </pre>

        <p>
            As you can see, both tables have timestamp-based TTLs, allowing us to configure the duration for which we
            want to retain profiling data.
        </p>

        <p>
            Now, let's examine the compression ratio.
            Below are statistics from our test environment with a few dozen services over 7 days:
        </p>

        <pre v-hl.sql>
SELECT
    table,
    formatReadableSize(sum(data_compressed_bytes) AS csize) AS compressed,
    formatReadableSize(sum(data_uncompressed_bytes) AS usize) AS uncompressed,
    round(usize / csize, 1) AS compression_ratio,
    sum(rows) AS rows
FROM system.parts
WHERE table LIKE 'profiling_%'
GROUP BY table

Query id: c7edc73e-c225-45ad-81dc-76d623cd078d

┌─table──────────────┬─compressed─┬─uncompressed─┬─compression_ratio─┬──────rows─┐
│ profiling_stacks   │ 931.50 MiB │ 13.68 GiB    │                15 │  16058148 │
│ profiling_samples  │ 6.19 GiB   │ 75.05 GiB    │              12.1 │ 923695809 │
└────────────────────┴────────────┴──────────────┴───────────────────┴───────────┘
        </pre>

        <p>
            As evident, the <var>profiling_stacks</var> table contains 60 times fewer rows than <var>profiling_samples</var>,
            highlighting the effectiveness of our manual optimization.
            Additionally, the compression ratios appear to be quite impressive.
        </p>

        <h2>Querying profiling data</h2>

        <p>
            Using the described schema, we can use the following query to aggregate all measurements for a given service and time interval.
            This query aggregates measurement values by <var>StackHash</var> and then retrieve the relevant stacks by their hashes.
        </p>


        <pre v-hl.sql>
WITH
    samples AS
    (
        SELECT
            StackHash AS hash,
            sum(Value) AS value
        FROM profiling_samples
        WHERE
            ServiceName IN ['/k8s/default/catalog'] AND
            Type = 'go:profile_cpu:nanoseconds' AND
            Start &lt; toDateTime64('2024-01-25 10:17:15.000000000', 9, 'UTC') AND
            End > toDateTime64('2024-01-25 09:17:15.000000000', 9, 'UTC')
        GROUP BY StackHash
    ),
    stacks AS
    (
        SELECT
            Hash AS hash,
            any(Stack) AS stack
        FROM profiling_stacks
        WHERE
            ServiceName IN ['/k8s/default/catalog'] AND
            LastSeen > toDateTime64('2024-01-25 09:17:15.000000000', 9, 'UTC')
        GROUP BY Hash
    )
SELECT
    value,
    stack
FROM samples
INNER JOIN stacks USING (hash)

...

1454 rows in set. Elapsed: 0.459 sec. Processed 968.75 thousand rows, 66.77 MB (2.11 million rows/s., 145.61 MB/s.)
        </pre>

        <p>
            In the result, we've obtained all the necessary data to render a FlameGraph.
            All aggregation across application instances and profiles is handled on the ClickHouse side,
            eliminating the need for additional computation on top of this data.
            The query latency is quite low and does not drastically change for longer time periods,
            as most of the time is spent on retrieving call stacks, and they are usually the same for any particular app.
        </p>

        <h2>Results</h2>

        <p>
            Using ClickHouse for storing profiling data has proven to meet all our storage requirements:
        </p>

        <ul>
            <li>Storage efficiency</li>
            <li>Configurable data retention</li>
            <li>Low latency</li>
            <li>Easy to support and maintain</li>
            <li>Permissive open-source license</li>
        </ul>

        <p>
            As a bonus, fully integrating ClickHouse as a storage solution for profiling data,
            along with our custom FlameGraph UI implementation, required less code compared to integrating with Pyroscope.
        </p>

        <p>
            Try <router-link :to="{name: 'cloud'}">Coroot Cloud</router-link> now (14-day free trial is available) or follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to install Coroot Community Edition.
        </p>

        <p>
            If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️.
        </p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>


export default {
    components: {},
}
</script>

<style scoped>
td {
    white-space: nowrap;
}
</style>
